import { useReactiveVar } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton/Skeleton'
import {
    isContentTeam,
    isOmWorkspaceUser,
    isWorkspaceEditable,
    setAddQueueSib,
    setIsAddOnQueue,
    setIsAddOngoing,
    setIsAddProhibited,
    workspaceContents,
    workspaceHasError,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React from 'react'
import { canElementBeShown } from 'shared/utils/bestPracticeUtils'
import { AreaContext } from './AreaContext'
import { Draggable } from './Draggable'
import { DRAGGABLE_ITEM_BORDER_RADIUS, DraggableItem } from './DraggableItem'
import {
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from './model'
import {
    ProductTypeInFamilyViewContentType,
    getProductTypeInFamilyViewContentType,
} from './utils'

export interface DroppableParentChildrenProps {
    type: string
    child: NormalizedDocumentTemplateViewItem[]
    updateParent: (newChildren: NormalizedDocumentTemplateViewItem[]) => void
    parent: NormalizedDocumentTemplateViewItem
    isArticleShared: boolean
    enableDragErrorColor?: boolean
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    selectedContent?: string
    setPromoteDemoteRequirements?: (input?: PromoteDemoteRequirements) => void
    setAreSharedElementsVisible: (value: boolean) => void
    projectProductFamilyId?: string
    isAddProhibited?: boolean
    isAddOngoing?: boolean
    contentItemNodes?: Partial<ContentItem>[]
    dtv: Partial<DocumentTemplateView> | null
    isNewAddOnQueue?: boolean
    addQueueSib?: string[] | undefined
    elementIdOnEdit?: string | undefined
    tabSelected: number
    parentProductTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
    familyAddedProductTypes?: ProjectElementNode[]
    productTypeNodeInFamily?: Partial<ProjectElementNode>
}

const InitialElementCount = 1
const ElementIncrementSize = 4
const ReducedIncrementSize = 2
const DepthLimit = 16

const useStyles = makeStyles((theme: Theme) => ({
    divContainer: {
        width: '100%',
        minHeight: 32,
        padding: theme.spacing(0.5),
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    dragBackground: {
        background: '#FFDEDE',
    },
    whiteBackground: {
        background: 'transparent',
    },
}))

export const extractChildIds = (
    items: NormalizedDocumentTemplateViewItem[]
): string[] => {
    let ids = items?.flatMap((child) => child?.id)

    items?.forEach((child) => {
        if (child?.children?.length > 0) {
            ids = ids?.concat(extractChildIds(child?.children))
        }
    })

    return ids
}

/**
 * Recursive functional component to display the parent and children templates
 * @param DroppableChildProps.type - type to be added to the Droppable; a.k.a source and destination index in DropResult
 * @param DroppableChildProps.child - child to be rendered to this item
 */
const DroppableParentChildren: React.FC<DroppableParentChildrenProps> = ({
    type,
    child,
    updateParent,
    parent,
    isArticleShared,
    enableDragErrorColor,
    contentTeamFromStudioPage,
    isDraftMode,
    selectedTopic,
    selectedContent,
    setPromoteDemoteRequirements,
    setAreSharedElementsVisible,
    projectProductFamilyId,
    isAddProhibited,
    isAddOngoing,
    contentItemNodes,
    dtv,
    isNewAddOnQueue,
    addQueueSib,
    elementIdOnEdit,
    tabSelected,
    parentProductTypeInFamilyViewContentType,
    familyAddedProductTypes,
    productTypeNodeInFamily,
}) => {
    const classes = useStyles()
    const isEditable = useReactiveVar(isWorkspaceEditable)
    const isAddingFailed = useReactiveVar(workspaceHasError)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)
    const { selectedProjectNode } = React.useContext(AreaContext)

    const doesChildContainsTopics = React?.useMemo(
        () => child?.some((item) => item?.isSustainabilityContentItem),
        [child]
    )

    const isSMMSustainabilityContentItem = React.useMemo(
        () =>
            !!(
                processEnv.REACT_APP_HideGroupSustainabilityContentInSMM !==
                    '1' &&
                isContentTeamUser &&
                contentTeamFromStudioPage &&
                !!doesChildContainsTopics
            ),
        [doesChildContainsTopics, isContentTeamUser, contentTeamFromStudioPage]
    )

    const isAEUserProjectWorkspace = React.useMemo(
        () =>
            !contentTeamFromStudioPage &&
            processEnv.REACT_APP_EnableVersionControl === '1' &&
            isOMUser,
        [contentTeamFromStudioPage, isOMUser, processEnv]
    )

    const addProperty = React.useCallback(
        (newProperty: NormalizedDocumentTemplateViewItem) => {
            let copyOfChildren = [...child]
            copyOfChildren.splice(
                newProperty.newProperties?.uiIndex ?? copyOfChildren.length,
                0,
                { ...newProperty, parentType: child[0].parentType }
            )

            if (copyOfChildren.every((child) => child.level === 2)) {
                copyOfChildren = copyOfChildren.map((childCopy, index) => {
                    if (newProperty.newProperties?.uiIndex === index) {
                        return {
                            ...newProperty,
                            parentType: child[0].parentType,
                        }
                    } else if (
                        childCopy.paragraphOrder &&
                        childCopy.paragraphOrder <
                            (newProperty.paragraphOrder as number)
                    ) {
                        return childCopy
                    } else if (childCopy?.isSustainabilityContentItem) {
                        return {
                            ...childCopy,
                            paragraphOrder:
                                (childCopy.paragraphOrder as number) + 1,
                            order: (childCopy.order as number) + 1,
                            children: childCopy?.children?.map((item) => ({
                                ...item,
                                order: (item.order as number) + 1,
                                paragraphOrder:
                                    (item.paragraphOrder as number) + 1,
                            })),
                        }
                    } else {
                        return {
                            ...childCopy,
                            paragraphOrder:
                                (childCopy.paragraphOrder as number) + 1,
                        }
                    }
                })
            }

            updateParent(copyOfChildren)
            setIsAddProhibited?.(true)
        },
        [child, updateParent, setIsAddProhibited, doesChildContainsTopics]
    )
    const checkPromoteDemoteRequirements = React.useCallback(
        (input?: PromoteDemoteRequirements) => {
            // Check and remove existing temporary paragraph
            if (input?.content?.id === 'temporaryTemplateId') {
                let copyOfChildren = [...child]
                copyOfChildren = copyOfChildren.filter(
                    (x) => x.id !== 'temporaryTemplateId'
                )
                updateParent(copyOfChildren)
            }
            setPromoteDemoteRequirements?.(input)
        },
        [setPromoteDemoteRequirements, updateParent, child]
    )

    const cancelAddProperty = React.useCallback(() => {
        let resultingArray = [...child]
        const newlyaddedIndex = resultingArray.findIndex(
            (child) => !!child.addingState
        )
        resultingArray = resultingArray.filter((child) => !!!child.addingState)

        if (newlyaddedIndex >= 0) {
            resultingArray = resultingArray.map((child, index) => {
                if (index > newlyaddedIndex) {
                    return {
                        ...child,
                        paragraphOrder: child.paragraphOrder! - 1,
                    }
                } else {
                    return {
                        ...child,
                        paragraphOrder: child.paragraphOrder,
                    }
                }
            })
        }

        updateParent(resultingArray)
        setIsAddProhibited?.(false)
        setIsAddOngoing?.(false)
        const { isNewAddOnQueue } = workspaceContents()
        if (isNewAddOnQueue) {
            setIsAddOnQueue(false)
            setAddQueueSib(undefined, true)
        }
    }, [child, updateParent])

    React.useEffect(() => {
        if (
            isAddingFailed &&
            child.some((property) => property.addingState === 'newlyAdded')
        ) {
            const newArticleIndex = child.findIndex(
                (article) => article.addingState
            )
            updateParent(
                child
                    ?.slice(0, newArticleIndex)
                    .concat({
                        ...child[newArticleIndex],
                        addingState: 'saved',
                        hasError: true,
                    })
                    .concat(child?.slice(newArticleIndex + 1))
            )
        }
    }, [isAddingFailed, child, updateParent])

    const [maxChild, setMaxChild] = React.useState(
        parent.level > 2 && child.length > InitialElementCount
            ? InitialElementCount
            : child.length
    )

    React.useEffect(() => {
        let counter = maxChild
        const interval = setInterval(() => {
            if (maxChild < child.length) {
                setMaxChild(counter)
                const startCount = counter
                counter += ElementIncrementSize

                if (counter > child.length) counter = child.length

                const depth = measureDepth(child.slice(startCount, counter))
                if (depth > DepthLimit && counter < child.length) {
                    counter -= ReducedIncrementSize
                }
            } else {
                clearInterval(interval)
            }
        }, 100)

        return () => clearInterval(interval)
    }, [maxChild, child])

    const droppableId = `droppableParentChild-${parent?.id}`

    // If the item is product-type-specific, any child properties the user adds
    // under it should also be product-type-specific; otherwise, they should be shared.
    const isPropertyShared: boolean = React.useMemo(
        () => (parent ? !parent.isForProductType : isArticleShared),
        [parent, isArticleShared]
    )

    const filteredArticleArray = React.useMemo(
        () =>
            child
                .slice(0, maxChild)
                .filter(
                    (art) => art.contentItem?.textType !== 'OfficeMasterNote'
                ),
        [child, maxChild]
    )

    // Fixes incorrect paragraph numbering caused by server re-indexing when OM siblings are deactivated.
    const omTemplateMap = React.useMemo<Map<string, NormalizedDocumentTemplateViewItem> | null>(() => {
        const hasOMDeactivated = child.some((x) => x.isDeactivated && !x.isHidden)
        if (!hasOMDeactivated || isSMMSustainabilityContentItem) {
            return null
        }
        return new Map(
            filteredArticleArray.map((x, i) => [x.id, { ...x, paragraphOrder: i + 1 }])
        )
    }, [child, isSMMSustainabilityContentItem, filteredArticleArray])

    // When in family view, If atleast one child is an added PT, returns true
    // Flag to use contentItemIndex in ContentItem due to issues in paragraphOrder
    const hasPTinFamilyChild = React.useMemo(
        () =>
            selectedProjectNode?.elementType === 'PRODUCTFAMILY' &&
            !!parentProductTypeInFamilyViewContentType &&
            !!filteredArticleArray.find(
                (filteredArticle) =>
                    !!filteredArticle.isProductGroup ||
                    !!filteredArticle.isProductType
            ),
        [
            filteredArticleArray,
            parentProductTypeInFamilyViewContentType,
            selectedProjectNode,
        ]
    )

    return (
        <SortableContext
            id={droppableId}
            items={child.slice(0, maxChild)}
            strategy={verticalListSortingStrategy}
        >
            <div
                className={clsx(
                    classes.divContainer,
                    enableDragErrorColor
                        ? classes.dragBackground
                        : classes.whiteBackground
                )}
            >
                {child
                    .slice(0, maxChild)
                    .filter((item) =>
                        canElementBeShown(item, isTrackChangeToggleOn)
                    )
                    .map((item, index) => {
                        const isParentPremiumPlus = parent.contentItem?.elements?.some(
                            (element) => !!element.isPremiumPlus
                        )

                        // Check if edit / drag should be disabled when PTP is in Family view
                        const ptpInFamilyContentType = hasPTinFamilyChild
                            ? getProductTypeInFamilyViewContentType(
                                  item as NormalizedDocumentTemplateViewItem,
                                  selectedProjectNode?.elementType,
                                  contentTeamFromStudioPage
                              )
                            : !!parentProductTypeInFamilyViewContentType
                            ? ProductTypeInFamilyViewContentType.PARAGRAPH
                            : undefined

                        return (
                            <React.Fragment key={item.id ?? ''}>
                                <Draggable
                                    isWrapped={
                                        !contentTeamFromStudioPage
                                            ? isEditable &&
                                              !item?.isHidden &&
                                              item?.type !== 'Table'
                                            : isDraftMode
                                    }
                                    id={item.id}
                                    index={index}
                                    droppableId={droppableId}
                                    childIds={extractChildIds(item?.children)}
                                    isUserAdded={item.isUserAdded ?? false}
                                >
                                    <DraggableItem
                                        template={omTemplateMap?.get(item.id) ?? item}
                                        isDraggable={
                                            (!contentTeamFromStudioPage
                                                ? isEditable && !item?.isHidden
                                                : isDraftMode) || false
                                        }
                                        selectedTopic={selectedTopic}
                                        parentType={type}
                                        contentItemIndex={
                                            !!isSMMSustainabilityContentItem
                                                ? item?.paragraphOrder ?? 0
                                                : filteredArticleArray.findIndex(
                                                      (
                                                          article: NormalizedDocumentTemplateViewItem
                                                      ) => article.id == item.id
                                                  )
                                        }
                                        addProperty={addProperty}
                                        updateChildInParent={updateParent}
                                        parent={parent}
                                        isPropertyShared={isPropertyShared}
                                        cancelAddProperty={cancelAddProperty}
                                        droppableId={droppableId}
                                        index={index}
                                        isLastChildOfParent={
                                            index === (child?.length ?? 1) - 1
                                        }
                                        contentTeamFromStudioPage={
                                            contentTeamFromStudioPage
                                        }
                                        isDraftMode={isDraftMode}
                                        selectedContent={selectedContent}
                                        setPromoteDemoteRequirements={
                                            checkPromoteDemoteRequirements
                                        }
                                        setAreSharedElementsVisible={
                                            setAreSharedElementsVisible
                                        }
                                        isBasedFromManufacturerOption={
                                            isParentPremiumPlus
                                        }
                                        projectProductFamilyId={
                                            projectProductFamilyId
                                        }
                                        isAddProhibited={isAddProhibited}
                                        isAddOngoing={isAddOngoing}
                                        contentItemNodes={contentItemNodes}
                                        dtv={dtv}
                                        isNewAddOnQueue={isNewAddOnQueue}
                                        addQueueSib={addQueueSib}
                                        elementIdOnEdit={elementIdOnEdit}
                                        tabSelected={tabSelected}
                                        productTypeInFamilyViewContentType={
                                            ptpInFamilyContentType
                                        }
                                        hiddenSiblingsCount={
                                            hasPTinFamilyChild
                                                ? filteredArticleArray
                                                      .slice(0, index)
                                                      .filter(
                                                          (x) =>
                                                              !!x.isHidden ||
                                                              (!!x.isDeactivated &&
                                                                  !!isAEUserProjectWorkspace)
                                                      ).length
                                                : 0
                                        }
                                        useOrderInContentItem={
                                            hasPTinFamilyChild
                                        }
                                        familyAddedProductTypes={
                                            familyAddedProductTypes
                                        }
                                        productTypeNodeInFamily={
                                            productTypeNodeInFamily
                                        }
                                    />
                                </Draggable>
                                {index === maxChild - 1 &&
                                    maxChild < child.length && (
                                        <div
                                            style={{
                                                width: '100',
                                                marginLeft: '25%',
                                            }}
                                        >
                                            <Skeleton
                                                variant="text"
                                                width="50%"
                                            />
                                        </div>
                                    )}
                            </React.Fragment>
                        )
                    })}
            </div>
        </SortableContext>
    )
}

export const measureDepth = (child: NormalizedDocumentTemplateViewItem[]) => {
    const count: number = child
        .map((childItem) =>
            childItem.children ? measureDepth(childItem.children) : 0
        )
        .reduce((acc: number, currentCount: number) => (acc += currentCount), 1)

    return count
}

export default DroppableParentChildren
